import { PaymentSystem } from '@web-solutions/react-billing';

/** Keys that only lead into transport noise (axios internals, serialized causes). */
const DECLINE_SEARCH_SKIP_KEYS = ['config', 'request', 'headers', 'cause', 'stack'];

const MAX_DECLINE_SEARCH_DEPTH = 5;

const DECLINE_CODE_KEYS = ['decline_code', 'declineCode'];

/** Solidgate reports its code as a number in some shapes, Stripe always as a string. */
const RESPONSE_CODE_KEYS = ['code', 'error_code', 'errorCode'];

const firstScalar = (...candidates: any[]): string | undefined => {
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate) {
      return candidate;
    }
    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return String(candidate);
    }
  }
  return undefined;
};

/**
 * A code shows up at a different depth per flow: top level on a Stripe.js
 * error, under `data` / `data.error` behind the billing interceptor
 * (packages/module-billing/api/network.js), under `last_payment_error` or
 * `payment_intent.last_payment_error` on a 200 with an unfinished intent.
 * Instead of listing every shape (and silently reporting nothing for the one
 * that was missed), walk the error and take the first match.
 */
const findErrorField = (error: any, keys: string[]): string | undefined => {
  const seen = new Set<any>();
  // Level by level, so the shallowest (= most relevant) value wins.
  let level: any[] = [error];

  for (let depth = 0; depth <= MAX_DECLINE_SEARCH_DEPTH && level.length; depth += 1) {
    const nextLevel: any[] = [];

    for (const value of level) {
      if (!value || typeof value !== 'object' || seen.has(value)) {
        continue;
      }

      seen.add(value);

      const own = firstScalar(...keys.map((key) => value[key]));

      if (own) {
        return own;
      }

      for (const [key, nested] of Object.entries(value)) {
        if (!DECLINE_SEARCH_SKIP_KEYS.includes(key)) {
          nextLevel.push(nested);
        }
      }
    }

    level = nextLevel;
  }

  return undefined;
};

/**
 * The provider's reason for refusing the charge (`insufficient_funds`,
 * `do_not_honor`, ...), as opposed to the umbrella `code` (`card_declined`).
 * Undefined when the response carries no decline reason (network failures,
 * validation errors).
 *
 * Stripe only: other providers don't send a separate decline reason (Solidgate
 * puts everything in its numeric `code`), so the caller must pass the payment
 * system and the field stays absent everywhere else.
 *
 * Reported as its own analytics field so a declined charge can be broken down
 * by bank reason instead of collapsing into `card_declined`.
 */
export const extractDeclineCode = (
  error: any,
  paymentSystem?: PaymentSystem | string,
): string | undefined => {
  if (paymentSystem !== PaymentSystem.STRIPE) {
    return undefined;
  }

  return findErrorField(error, DECLINE_CODE_KEYS);
};

/**
 * The payment system's own response code — Stripe's umbrella `card_declined`,
 * Solidgate's numeric code, an axios `ERR_BAD_RESPONSE` on a transport failure.
 *
 * Searched at every depth for the same reason the decline reason is. Reading
 * only `error.code` reported nothing for every shape where the code sits deeper
 * — which is how a declined charge reached the analytics with `decline_code`
 * filled and `code` empty, the two being extracted asymmetrically.
 *
 * Unlike the decline reason this is not gated on the payment system: every
 * provider sends a code.
 */
export const extractResponseCode = (error: any): string | undefined =>
  findErrorField(error, RESPONSE_CODE_KEYS);
